import React, { useEffect, useState } from "react";
import { Col, Container, Row } from "reactstrap";
import apiGlobal from "../global/api.global";
import * as Yup from "yup";
import "../global/GlobalCSS.css";
import Layout from "../HorizontalMenu/Menu";
import Navbar from "../HorizontalMenu/Navbar";
import VesselDetailsHeader from "../Components/VesselDetailsHeader";
import { useDispatch, useSelector } from "react-redux";
import {
    VesselState,
    getConfigurationLimitsAction,
    getVesselsAction,
    setVesselState,
    setVoyageID
} from "../Store/Generic/ReportingSlice";
import { PortConstant, VoyageConstant } from "shared/constants";
import {
    Get,
} from "VesselMaster/vesselMaster.hooks";
import { useQuery } from "react-query";
import { queryKeyes } from "shared/queryKeys";
import { commonValidationMessages } from "Components/ValidationErrorMessages";
import { queryClient } from "react-query/queryClient";
import CreateVoyageStructure from "./CreateVoyageStructure";
import env from 'environment_system/env_system';
import { useDocumentTitle } from "Components/useDocument.hooks";
import { dynamicSetFieldErrors, handleServerResponse } from "GenericForms/Helper";

interface CreateVoyageType {
    voyageId?: number;
    toggleTab?: (activeTab: number) => void,
    activeTab?: number
    isFirstReport?: boolean
}

const CreateVoyage = ({
    voyageId,
    toggleTab,
    activeTab,
    isFirstReport = false
}: CreateVoyageType) => {
    /** State variables */
    const dispatch = useDispatch();
    const { VesselState, VesselID } = useSelector(
        (state: any) => state.Reporting
    );
    const [otherPort, setOtherPort] = useState(false);
    const [addOtherPort, setAddOtherPort] = useState(false);
    const [documentTitle, setDocumentTitle] = useState<any>(null);
    const [errorMessage, setErrorMessage] = useState<any>()
    useDocumentTitle(documentTitle);
    /** State variables end */

    /** useQueries */
    /** Voyage object used for edit */
    const {
        data: VoyageObject,
        isLoading: VoyageObjectLoading,
    } = useQuery(
        [
            isFirstReport
                ? queryKeyes.vessel.GetOnboardingVoyageByVessel.key
                : queryKeyes.vessel.VoyageObject.key,
            isFirstReport ? VesselID : voyageId
        ],
        async () => {
            return isFirstReport
                ? await Get(queryKeyes.vessel.GetOnboardingVoyageByVessel.url(VesselID), VesselID)
                : await Get(queryKeyes.vessel.VoyageObject.url(voyageId), voyageId);
        },
        // { staleTime: Infinity }
    );

    /** Other Ports from master */
    const {
        data: OtherPortsObject,
        isLoading: OtherPortsLoading,
    }: { data: any; isLoading: any; isError: any } = useQuery(
        [queryKeyes.masters.OtherPortMaster.key],
        async () => {
            return await Get(queryKeyes.masters.OtherPortMaster.url())
        },
        { staleTime: Infinity }
    );
    /** useQueries end */

    /** useEffect start */
    useEffect(() => {
        dispatch(getVesselsAction("vessel_master" as string));
    }, [dispatch]);

    useEffect(() => {
        dispatch(getConfigurationLimitsAction(VesselID));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [VesselID]);

    useEffect(() => {
        setDocumentTitle(voyageId ? 'Edit Voyage - ecoSAIL' : (isFirstReport ? 'Create Voyage - Onboarding - ecoSAIL' : 'Create Voyage - ecoSAIL'));
    }, [voyageId, isFirstReport]);
    /** useEffect end */


    /** Assigning initial object to voyage's formik object */
    const getInitialValues = () => {
        if (VoyageObject && VoyageObject?.id > 0) {
            return VoyageObject
        } else {
            return {
                voyage_number: "",
                voyage_status: VoyageConstant.NEWVOYAGE,
                departure_port: null,
                other_port: null as number,
                other_port_name: null as string,
                other_country: null as number,
                other_country_name: null as string,
                vessel: VesselID,
            };
        }
    };

    /**useEffect start */
    useEffect(() => {
        if (VoyageObject && VoyageObject?.id > 0) {
            if (VoyageObject?.port_precedence_id === PortConstant.OTHER) {
                setOtherPort(true);
            }
        }
    }, [VoyageObject]);
    /** useEffect end */

    /** Voyage Information formik object */
    const VoyageFormik = {
        initialValues: getInitialValues(),
        validationSchema: Yup.object({
            voyage_number: Yup.string()
                .matches(
                    /^(?![-/])[A-Za-z0-9-/&]{1,50}$/,
                    "Please enter up to 50 alphabets/numbers only"
                )
                .required(commonValidationMessages.required),
            departure_port: Yup.string().required(commonValidationMessages.required),
            other_port: Yup.string().when(
                "$fieldAvailability",
                (field: any, schema) => {
                    return !(
                        otherPort === true &&
                        OtherPortsObject &&
                        OtherPortsObject?.length > 0
                    )
                        ? schema.nullable()
                        : schema.required(commonValidationMessages.required);
                }
            ),
            other_port_name: Yup.string().when(
                "$fieldAvailability",
                (field: any, schema) => {
                    return !(addOtherPort === true || !OtherPortsObject)
                        ? schema.nullable()
                        : schema.required(commonValidationMessages.required);
                }
            ),
            other_country: Yup.number().when(
                "$fieldAvailability",
                (field: any, schema) => {
                    return !(addOtherPort === true || !OtherPortsObject)
                        ? schema.nullable()
                        : schema.required(commonValidationMessages.required);
                }
            ),
        }),
    };
    const handleVesselState = (record: VesselState) => {
        dispatch(setVesselState(record));
    };

    const responseArray: any[] = [];
    /** POST request for Voyage Information */
    const postVoyage = (values: any) => {
        responseArray.push(apiGlobal.post(isFirstReport ?
            queryKeyes.vessel.OnboardVoyageObject.url() : queryKeyes.vessel.VoyageInformation.url(), values
        ));
    };


    /** PUT request for Voyage Information */
    const putVoyage = (values: any) => {
        responseArray.push(apiGlobal.put(`${isFirstReport ?
            `${queryKeyes.vessel.OnboardVoyageObject.url()}` : `${queryKeyes.vessel.VoyageInformation.url()}`}${values?.id}/`, values
        ));
    };

    /** Voyage submit function */
    const voyageSubmit = async (values: any, actions: any) => {
        actions.setSubmitting(true);
        if (VoyageObject && VoyageObject?.id > 0) {
            putVoyage(values);
        } else {
            postVoyage(values);
        }
        responseArray && responseArray[0].then((res: any) => {
            if (VesselState !== 'CREATE_VOYAGE_REPORTING' || VesselState !== 'VOYAGE_REPORTING_EDIT') {
                dispatch(setVoyageID(res.data.id));
            }
        }).catch((err: any) => {
            console.error(err)
        })
        await handleServerResponse(responseArray).then(async (res) => {
            if (res === true) {
                if (isFirstReport) {
                    await queryClient.invalidateQueries(queryKeyes.vessel.GetOnboardingVoyageByVessel.key);
                    if (addOtherPort && OtherPortsObject &&
                        OtherPortsObject?.filter((port: any) =>
                            port?.port_name === values?.other_port_name)?.length === 0) {
                        await queryClient.invalidateQueries(queryKeyes.masters.OtherPortMaster.key);
                    }
                    if (env.form_validation === true) {
                        setErrorMessage(null);
                        toggleTab(activeTab + 1);
                    }
                    actions.setSubmitting(false);
                } else {
                    await queryClient.invalidateQueries(queryKeyes.vessel.VoyageObject.key);
                    if (addOtherPort && OtherPortsObject &&
                        OtherPortsObject?.filter((port: any) =>
                            port?.port_name === values?.other_port_name)?.length === 0) {
                        await queryClient.invalidateQueries(queryKeyes.masters.OtherPortMaster.key);
                    }
                    handleVesselState("VOYAGE_REPORTING");
                    actions.setSubmitting(false);
                }
            } else {
                setErrorMessage(res);
                dynamicSetFieldErrors(res, actions, false);
                actions.setSubmitting(false);
            }
            actions.setSubmitting(false);
        })
    };
    return (
        <React.Fragment>
            <Layout>
                <Navbar />
            </Layout>
            <div className="page-content">
                <Container fluid>
                    <Row>
                        <Col sm={2}>
                            <button
                                color="primary"
                                className="btn btn-primary mb-3"
                                onClick={() => {
                                    handleVesselState(isFirstReport ? null : "VOYAGE_REPORTING");
                                }}
                            >
                                <i className="bx bx-chevron-left me-1" />
                                Back
                            </button>
                        </Col>
                        <Col sm={10}>
                            <VesselDetailsHeader />
                        </Col>
                    </Row>
                    <CreateVoyageStructure
                        VoyageObjectLoading={VoyageObjectLoading}
                        OtherPortsLoading={OtherPortsLoading}
                        voyageSubmit={voyageSubmit as any}
                        VoyageFormik={VoyageFormik}
                        otherPort={otherPort}
                        setOtherPort={setOtherPort}
                        addOtherPort={addOtherPort}
                        setAddOtherPort={setAddOtherPort}
                        OtherPortsObject={OtherPortsObject}
                        VoyageObject={VoyageObject}
                        toggleTab={toggleTab}
                        activeTab={activeTab}
                        isFirstReport={isFirstReport}
                        errorMessage={errorMessage}
                        setErrorMessage={setErrorMessage}
                    />
                </Container>
            </div>
        </React.Fragment>
    );
};

export default CreateVoyage;

